import React, { useState, useEffect, useRef } from "react";
import { tailwindOverride } from "./tailwindOverride";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import fpFetch from "@/services/fpFetch";
// ../utils/addOnServices/highlightTextNodes.ts
import {
  highlightDocx,
  triggerHighlight,
  clearHighlights,
} from "./docxHighlighter";

interface ResponseLike {
  json(): Promise<any>;
}

const DocxViewer = ({
  fileUrl,
  fileContentResponse,
}: {
  fileUrl: string;
  fileContentResponse: ResponseLike | undefined;
}) => {
  const [content, setContent] = useState<{ html: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const fileViewerData = useSelector(
    (state: RootState) => state.fileViewerData
  );
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchFileContent = async () => {
      if (!fileUrl) return;

      setIsLoading(true);
      try {
        if (fileContentResponse) {
          const result = await fileContentResponse.json();
          setContent(result);
          return;
        }
        const response = await fpFetch(fileUrl);
        const result = await response.json();
        setContent(result);
      } catch (err) {
        console.error("Failed to fetch file content", err);
        setError("Failed to load DOCX file");
      } finally {
        setIsLoading(false);
      }
    };

    fetchFileContent();
  }, [fileUrl]);

  useEffect(() => {
    if (!container.current) return;
    if (content) {
      const pageNum = fileViewerData.highlightPageNum;
      const pageElement = document.querySelector(`.docx-page-${pageNum}`);
      if (pageElement) {
        pageElement.scrollIntoView({ behavior: "smooth" });
      }
      if (!fileViewerData.highlightContext) return;
      const didHighlight = highlightDocx(
        container.current,
        fileViewerData.highlightContext,
        String(fileViewerData.originalNumValue),
        { exactOnly: fileViewerData.exactOnly }
      );
      // A table cell wants the value where the page prints it, or nothing
      // (#12090): no whole-page fallback for it.
      if (!didHighlight && !fileViewerData.exactOnly) {
        const page = container.current.querySelector(
          ".docx-page-" + String(fileViewerData.highlightPageNum)
        );
        if (page) {
          triggerHighlight(page, "div");
        }
      }
    }
  }, [content, fileViewerData]);

  if (isLoading) {
    return <div className="loading p-6 text-gray-500">Loading document...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div
      className="w-full h-full overflow-auto p-4 px-12 docx-viewer"
      onClick={(e) => {
        // Check if the clicked element is a link or contained within a link
        const target = e.target as HTMLElement;
        const link = target.closest("a");

        if (link && link.href) {
          e.preventDefault();
          e.stopPropagation();
          window.open(link.href, "_blank", "noopener,noreferrer");
          return;
        }

        if (container.current) {
          clearHighlights(container.current);
        }
      }}
    >
      <style>{tailwindOverride}</style>
      <div ref={container} className="pb-20">
        {content.map((item, index) => (
          <div key={index} className="mb-8">
            <div
              className={` tailwind-override docx-page-${index + 1}`}
              dangerouslySetInnerHTML={{ __html: item.html }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default DocxViewer;
